import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom"; // ← ADD
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Stack,
  Tooltip,
  Chip,
  TextField,
  Autocomplete,
  FormControlLabel,
  Popover,
  CircularProgress,
  useTheme,
  useMediaQuery,
  TableFooter,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  LocationOn,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import {
  getFreightsByBilPartyIdApi,
  updateFreight,
  billCreate,
  billUpdate,
  freightDelete,
  freightPrint,
  partyList,
} from "../../../Services/Services";
import { printLorryReceipt } from "./billCreationPDFGenerator";
import { T } from "../../../theme";

// ── All your existing style constants (sq, primaryBtn, etc.) ──────
const sq = { borderRadius: "4px !important" };
const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.75rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
  "&.Mui-disabled": { bgcolor: `${T.primary}50`, color: T.white },
};
const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};
const filterInputSx = {
  "& .MuiInputBase-root": { height: 36, ...sq },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};
const disabledFieldSx = {
  "& .MuiInputBase-root": { height: 36, ...sq, bgcolor: `${T.textMuted}18` },
  "& fieldset": { border: `1.5px solid ${T.border}` },
};
const rateInputSx = {
  "& .MuiInputBase-root": {
    height: 26,
    fontSize: "0.68rem",
    ...sq,
    bgcolor: T.white,
  },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& .MuiInputBase-root:hover fieldset": {
    border: `1.5px solid ${T.borderHover}`,
  },
  "& .MuiInputBase-root.Mui-focused fieldset": {
    border: `1.5px solid ${T.primary}`,
  },
};
const TABLE_COLUMN_COUNT = 12;

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

const isNumericRate = (val) => {
  const str = String(val).trim();
  if (str === "") return true;
  return !isNaN(str) && !isNaN(parseFloat(str));
};

const LabelValue = ({ label, value, color }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "0.58rem",
        color: T.textMuted,
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "0.7rem", fontWeight: 600, color: color || T.text }}
    >
      {value}
    </Typography>
  </Box>
);

// ── MobileBillCard ─────────────────────────────────────────────────
const MobileBillCard = ({
  row,
  idx,
  expanded,
  onToggle,
  selected,
  onSelect,
  onEdit,
  onDelete,
  onPrint,
  packageRates,
  onRateChange,
  fixedAmounts,
  onFixedAmountChange,
}) => {
  const subtotal = (row.package_items || []).reduce((sum, pkg, pkgIdx) => {
    const rateKey = `${row.freight_id}_${pkgIdx}`;
    const rateVal = packageRates[rateKey] ?? pkg.rate ?? "0";
    const weight =
      parseFloat(pkg.charged_weight || pkg.actual_weight || 0) || 0;

    if (isNumericRate(rateVal)) {
      return sum + (parseFloat(rateVal) || 0) * weight;
    } else {
      return sum + (parseFloat(fixedAmounts?.[rateKey]) || 0);
    }
  }, 0);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${expanded ? T.primary : selected ? T.success : T.border}`,
        bgcolor: selected ? `${T.success}06` : T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      {/* Card Header */}
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        <Box
          sx={{
            width: 55,
            flexShrink: 0,
            bgcolor: expanded ? T.primary : T.primaryBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          <Checkbox
            size="small"
            checked={selected}
            onChange={onSelect}
            sx={{
              p: 0.25,
              color: expanded ? T.white : T.primary,
              "&.Mui-checked": { color: T.success },
            }}
          />
          <Typography
            sx={{
              fontSize: "0.46rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            px: 1.2,
            py: 0.75,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.85rem",
              color: T.text,
              lineHeight: 1.2,
              mb: 0.15,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.truck_no || "—"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.62rem",
              color: T.textSub,
              fontWeight: 500,
              mb: 0.15,
            }}
          >
            LR: {row.lr_number || "—"} | {formatDate(row.loading_date)}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography
              sx={{
                fontSize: "0.58rem",
                color: T.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.from_location || "—"} → {row.to_location || "—"}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-end",
            px: 1,
            py: 0.75,
          }}
        >
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 22,
              fontSize: "0.55rem",
              px: 0.7,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "View"}
          </Button>
        </Box>
      </Box>

      {/* Expanded detail */}
      {expanded && (
        <Box
          sx={{
            bgcolor: T.primaryBg,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.25,
          }}
        >
          <Stack spacing={0.75}>
            {(row.package_items || []).map((pkg, pkgIdx) => {
              const rateKey = `${row.freight_id}_${pkgIdx}`;
              const rate = packageRates[rateKey] ?? pkg.rate ?? "0";
              const weight = pkg.charged_weight || pkg.actual_weight || 0;
              const numeric = isNumericRate(rate);
              const amt = numeric
                ? ((parseFloat(rate) || 0) * parseFloat(weight)).toFixed(2)
                : (parseFloat(fixedAmounts?.[rateKey]) || 0).toFixed(2);

              return (
                <Box
                  key={pkgIdx}
                  sx={{
                    p: 0.75,
                    bgcolor: T.white,
                    border: `1px solid ${numeric ? T.border : "#f59e0b"}`,
                    ...sq,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      color: T.primary,
                      mb: 0.5,
                    }}
                  >
                    📦 {pkg.packages || "Package"} {pkgIdx + 1}
                    {pkg.description ? ` — ${pkg.description}` : ""}
                  </Typography>
                  <Grid container spacing={0.5} alignItems="center">
                    <Grid item xs={6}>
                      <LabelValue
                        label="Actual Wt"
                        value={pkg.actual_weight || 0}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <LabelValue
                        label="Charged Wt"
                        value={pkg.charged_weight || 0}
                      />
                    </Grid>

                    {/* ★ Rate Field — type="text" */}
                    <Grid item xs={6}>
                      <Typography
                        sx={{
                          fontSize: "0.58rem",
                          color: T.textMuted,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          mb: 0.25,
                        }}
                      >
                        Rate
                      </Typography>
                      <TextField
                        size="small"
                        type="text"
                        value={rate}
                        onChange={(e) => onRateChange(rateKey, e.target.value)}
                        placeholder="Rate or Fixed"
                        sx={{
                          ...rateInputSx,
                          ...(!numeric && {
                            "& .MuiInputBase-root": {
                              bgcolor: "#fffbeb",
                              border: "1.5px solid #f59e0b",
                              height: 26,
                              fontSize: "0.68rem",
                              ...sq,
                            },
                          }),
                        }}
                      />
                    </Grid>

                    {/* ★ Amount Field — editable when not numeric */}
                    <Grid item xs={6}>
                      <Typography
                        sx={{
                          fontSize: "0.58rem",
                          color: T.textMuted,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          mb: 0.25,
                        }}
                      >
                        Amount
                      </Typography>
                      {numeric ? (
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: T.primary,
                          }}
                        >
                          ₹{amt}
                        </Typography>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={fixedAmounts?.[rateKey] ?? ""}
                          onChange={(e) =>
                            onFixedAmountChange(rateKey, e.target.value)
                          }
                          placeholder="₹ Amount"
                          sx={{
                            ...rateInputSx,
                            "& .MuiInputBase-root": {
                              bgcolor: "#fffbeb",
                              border: "1.5px solid #fbbf24",
                              height: 26,
                              fontSize: "0.68rem",
                              ...sq,
                            },
                          }}
                          inputProps={{ min: 0 }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              );
            })}

            {/* Subtotal for mobile card */}
            {(row.package_items || []).length > 0 && (
              <Box
                sx={{
                  p: 0.75,
                  bgcolor: T.subtotalBg,
                  border: `1px solid ${T.subtotalBorder}`,
                  ...sq,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: T.subtotalText,
                    textTransform: "uppercase",
                    letterSpacing: "0.03em",
                  }}
                >
                  Subtotal — LR: {row.lr_number || "—"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    color: T.subtotalText,
                  }}
                >
                  {formatCurrency(subtotal)}
                </Typography>
              </Box>
            )}

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                pt: 0.75,
                borderTop: `1px dashed ${T.border}`,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Button
                size="small"
                startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
                onClick={() => onPrint(row)}
                sx={{
                  ...sq,
                  bgcolor: `${T.success}14`,
                  color: T.success,
                  fontSize: "0.65rem",
                  textTransform: "none",
                  px: 1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: `${T.success}28`, boxShadow: "none" },
                }}
              >
                Print
              </Button>
              <Button
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 13 }} />}
                onClick={() => onEdit(row)}
                sx={{
                  ...sq,
                  bgcolor: `${T.primary}14`,
                  color: T.primary,
                  fontSize: "0.65rem",
                  textTransform: "none",
                  px: 1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: `${T.primary}28`, boxShadow: "none" },
                }}
              >
                Edit
              </Button>
              <Button
                size="small"
                startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
                onClick={(e) => onDelete(e, row.freight_id)}
                sx={{
                  ...sq,
                  bgcolor: `${T.red}14`,
                  color: T.red,
                  fontSize: "0.65rem",
                  textTransform: "none",
                  px: 1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: `${T.red}28`, boxShadow: "none" },
                }}
              >
                Delete
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

// ── Main Component ─────────────────────────────────────────────────
const BillCreationForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation(); // ← NEW
  const navigate = useNavigate(); // ← NEW

  // ── Detect edit mode from navigation state ──────────────────────
  const editState = location.state; // passed from BillSummaryTable
  const isEditMode = editState?.isEditMode || false;

  // ── All your existing state ─────────────────────────────────────
  const [selectedFreightIds, setSelectedFreightIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [partyOptions, setParties] = useState([]);
  const [freightOptions, setFreightOptions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState({});
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({ branch_id: null, party_id: null });
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [packageRates, setPackageRates] = useState({});
  const [haltingCharge, setHaltingCharge] = useState(0);
  const [haltingMessage, setHaltingMessage] = useState("");
  const [fixedAmounts, setFixedAmounts] = useState({});
  const [tdsPercent, setTdsPercent] = useState(0);
  const [tdsAmount, setTdsAmount] = useState(0);
  const [deductionAmount, setDeductionAmount] = useState(0);
  const [deductionRemarks, setDeductionRemarks] = useState("");

  useEffect(() => {
    if (!isEditMode || !editState) return;
    const { billingData, freights, selectedFreightIds: selIds } = editState;

    setFormData({
      branch_id: billingData.branch_id || null,
      party_id: billingData.party_id,
    });
    setHaltingCharge(billingData.halting_charge || 0);
    setHaltingMessage(billingData.halting_message || "");
    setTdsPercent(billingData.tds_percent || 0);
    setTdsAmount(billingData.tds_amount || 0);
    setDeductionAmount(billingData.deduction_amount || 0);
    setDeductionRemarks(billingData.deduction_remarks || "");
    // ───────────────────────────────────────────────────────────

    setFreightOptions(freights || []);
    setSelectedFreightIds(selIds || []);

    const initRates = {};
    const initFixedAmts = {};
    (freights || []).forEach((row) => {
      (row.package_items || []).forEach((pkg, idx) => {
        const key = `${row.freight_id}_${idx}`;
        const rateVal = String(pkg.rate ?? "0");
        initRates[key] = rateVal;
        if (!isNumericRate(rateVal)) initFixedAmts[key] = pkg.amount || 0;
      });
    });
    setPackageRates(initRates);
    setFixedAmounts(initFixedAmts);
  }, []);

  // ── Fetch parties ───────────────────────────────────────────────
  useEffect(() => {
    const fetchParties = async () => {
      try {
        const [consignors, consignees, brokers] = await Promise.all([
          partyList({ party_type: "consignor" }),
          partyList({ party_type: "consignee" }),
          partyList({ party_type: "broker" }),
        ]);

        const tagged = [
          ...(Array.isArray(consignors) ? consignors : []).map((p) => ({
            ...p,
            party_type: "consignor",
          })),
          ...(Array.isArray(consignees) ? consignees : []).map((p) => ({
            ...p,
            party_type: "consignee",
          })),
          ...(Array.isArray(brokers) ? brokers : []).map((p) => ({
            // ← NEW
            ...p,
            party_type: "broker",
          })),
        ];

        setParties(tagged);
      } catch (error) {
        console.error("Failed to fetch parties:", error);
        setParties([]);
      }
    };
    fetchParties();
  }, []);
  // ── Handlers same as your existing ─────────────────────────────
  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  const handleRateChange = (key, value) =>
    setPackageRates((prev) => ({ ...prev, [key]: value }));

  const handleFixedAmountChange = (key, value) =>
    setFixedAmounts((prev) => ({ ...prev, [key]: value }));

  const getRowSubtotal = (row) =>
    (row.package_items || []).reduce((sum, pkg, idx) => {
      const rateKey = `${row.freight_id}_${idx}`;
      const rateVal = packageRates[rateKey] ?? pkg.rate ?? "0";
      const weight =
        parseFloat(pkg.charged_weight || pkg.actual_weight || 0) || 0;
      if (isNumericRate(rateVal))
        return sum + (parseFloat(rateVal) || 0) * weight;
      return sum + (parseFloat(fixedAmounts[rateKey]) || 0);
    }, 0);

  const getFreightsByPartyId = async (party_id, party_type = "consignor") => {
    setLoading(true);
    try {
      const response = await getFreightsByBilPartyIdApi(party_id, party_type); // ← pass type
      setFreightOptions(response);
      setSelectedFreightIds([]);
      setSelectAll(false);
      setExpandedCardId(null);

      const initRates = {};
      const initFixedAmts = {};
      (response || []).forEach((row) => {
        (row.package_items || []).forEach((pkg, idx) => {
          const key = `${row.freight_id}_${idx}`;
          const rateVal = String(pkg.rate ?? "0");
          initRates[key] = rateVal;
          if (!isNumericRate(rateVal)) initFixedAmts[key] = pkg.amount || 0;
        });
      });
      setPackageRates(initRates);
      setFixedAmounts(initFixedAmts);
    } catch {
      setFreightOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowSelect = (freight_id) =>
    setSelectedFreightIds((prev) =>
      prev.includes(freight_id)
        ? prev.filter((id) => id !== freight_id)
        : [...prev, freight_id],
    );

  const handleSelectAll = () => {
    setSelectedFreightIds(
      selectAll ? [] : freightOptions.map((f) => f.freight_id),
    );
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    setSelectAll(
      selectedFreightIds.length === freightOptions.length &&
        freightOptions.length > 0,
    );
  }, [selectedFreightIds, freightOptions]);

  // ── Build payload (same as existing) ───────────────────────────
  const buildPayload = () =>
    freightOptions
      .filter((r) => selectedFreightIds.includes(r.freight_id))
      .map((row) => ({
        freight_id: row.freight_id,
        package_items: (row.package_items || []).map((pkg, idx) => {
          const rateKey = `${row.freight_id}_${idx}`;
          const rateVal = packageRates[rateKey] ?? pkg.rate ?? "0";
          const weight =
            parseFloat(pkg.charged_weight || pkg.actual_weight || 0) || 0;

          if (isNumericRate(rateVal)) {
            const rate = parseFloat(rateVal) || 0;
            return {
              package_id: pkg.package_id,
              rate: String(rate),
              amount: parseFloat((rate * weight).toFixed(2)),
            };
          }
          return {
            package_id: pkg.package_id,
            rate: String(rateVal),
            amount: parseFloat(fixedAmounts[rateKey]) || 0,
          };
        }),
      }));

  const grandTotal = freightOptions.reduce(
    (s, row) => s + getRowSubtotal(row),
    0,
  );

  const netPayableTotal = selectedFreightIds.reduce((acc, id) => {
    const row = freightOptions.find((f) => f.freight_id === id);
    return row ? acc + getRowSubtotal(row) : acc;
  }, 0);

  const grandTotalWithHalting = grandTotal + (parseFloat(haltingCharge) || 0);
  const netPayableTotalWithHalting =
    netPayableTotal + (parseFloat(haltingCharge) || 0);

  useEffect(() => {
    const pct = parseFloat(tdsPercent) || 0;
    if (pct === 0) return;
    const calculated = parseFloat(
      ((netPayableTotalWithHalting * pct) / 100).toFixed(2),
    );
    setTdsAmount(calculated);
  }, [tdsPercent, netPayableTotalWithHalting]);

  const tdsAmountFinal = parseFloat(tdsAmount) || 0;
  const deductionAmountFinal = parseFloat(deductionAmount) || 0;

  const finalNetPayable = parseFloat(
    (
      netPayableTotalWithHalting -
      tdsAmountFinal -
      deductionAmountFinal
    ).toFixed(2),
  );

  const alreadyPaid = isEditMode
    ? parseFloat(editState?.billingData?.paid_amount) || 0
    : 0;
  const balanceAmount = parseFloat((finalNetPayable - alreadyPaid).toFixed(2));

  const handleCreate = async () => {
    const errors = {};
    if (!formData.party_id) errors.party_id = "Party is required";
    if (selectedFreightIds.length === 0)
      errors.freight_id = "At least one LR must be selected";
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      const response = await billCreate({
        branch_id: formData.branch_id,
        party_id: formData.party_id,
        freight_id: selectedFreightIds,
        freight_amount: netPayableTotal,
        halting_charge: parseFloat(haltingCharge) || 0,
        halting_message: haltingMessage || "",
        total_amount: grandTotalWithHalting,

        // ── NEW FIELDS ────────────────────────────────────────────
        tds_percent: parseFloat(tdsPercent) || 0,
        tds_amount: tdsAmountFinal,
        deduction_amount: deductionAmountFinal,
        deduction_remarks: deductionRemarks || "",
        // ─────────────────────────────────────────────────────────

        rem_amount: balanceAmount, // ← uses new formula now
        freights: buildPayload(),
      });

      if (response) {
        setSnackbar({
          open: true,
          message: "Bill created successfully!",
          severity: "success",
        });
        // reset all including new fields
        setFormData({ branch_id: null, party_id: null });
        setSelectedFreightIds([]);
        setFreightOptions([]);
        setPackageRates({});
        setFixedAmounts({});
        setHaltingCharge(0);
        setHaltingMessage("");
        setTdsPercent(0); // ← NEW
        setTdsAmount(0); // ← NEW
        setDeductionAmount(0); // ← NEW
        setDeductionRemarks(""); // ← NEW
        setSelectAll(false);
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to create bill",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── UPDATE handler (NEW — for edit mode) ───────────────────────
  const handleUpdate = async () => {
    const errors = {};
    if (!formData.party_id) errors.party_id = "Party is required";
    if (selectedFreightIds.length === 0)
      errors.freight_id = "At least one LR must be selected";
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      const billingId = editState.billingData.billing_id;
      const paidAmount = parseFloat(editState.billingData.paid_amount) || 0;

      const freight_data = freightOptions.map((row) => ({
        freight_id: row.freight_id,
        is_bill: selectedFreightIds.includes(row.freight_id),
      }));

      const payload = {
        party_id: formData.party_id,
        freight_id: selectedFreightIds,
        freight_amount: netPayableTotal,
        total_amount: grandTotalWithHalting,
        halting_charge: parseFloat(haltingCharge) || 0,
        halting_message: haltingMessage || "",
        tds_percent: parseFloat(tdsPercent) || 0,
        tds_amount: tdsAmountFinal,
        deduction_amount: deductionAmountFinal,
        deduction_remarks: deductionRemarks || "",
        paid_amount: paidAmount,
        rem_amount: balanceAmount,
        freight_data: freight_data,
        freights: buildPayload(),
      };

      const res = await billUpdate(billingId, payload);
      if (res) {
        setSnackbar({
          open: true,
          message: "Bill updated successfully!",
          severity: "success",
        });
        setTimeout(() => navigate("/dashboard/bill-list"), 1000);
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to update bill",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Single submit handler — delegates to create or update ───────
  const handleSubmit = () => {
    if (isEditMode) handleUpdate();
    else handleCreate();
  };

  // ── Edit / Delete / Print handlers same as existing ─────────────
  const handleEditOpen = (row) => {
    setEditRow({ ...row });
    setEditDialogOpen(true);
  };
  const handleEditChange = (field, value) => {
    // ... your existing handleEditChange logic unchanged
  };
  const handleEditSave = async () => {
    const saveData = { ...editRow };
    saveData.receive_amount = saveData.receive_amount || 0;
    saveData.bal_amount = (
      parseFloat(saveData.net_payable || 0) -
      parseFloat(saveData.paid_amount || 0)
    ).toFixed(2);
    const res = await updateFreight(saveData.freight_id, saveData);
    setFreightOptions((prev) =>
      prev.map((r) => (r.freight_id === res.freight_id ? res : r)),
    );
    setEditDialogOpen(false);
    setSnackbar({
      open: true,
      message: "LR updated successfully!",
      severity: "success",
    });
  };
  const handleDelete = async (id) => {
    const del = await freightDelete(id);
    setFreightOptions((prev) =>
      prev.filter((r) => r.freight_id !== del.freight_id),
    );
    setSnackbar({
      open: true,
      message: "LR deleted successfully!",
      severity: "success",
    });
    setDeleteAnchorEl(null);
    setDeleteId(null);
  };
  const handlePrintSingleRow = async (row) => {
    try {
      const printData = await freightPrint(row.freight_id);
      if (printData?.length > 0) {
        printLorryReceipt({
          ...row,
          ...printData[0],
          package_items: printData[0]?.package_items || row.package_items || [],
        });
      } else {
        printLorryReceipt(row);
      }
    } catch {
      printLorryReceipt(row);
    }
  };

  const editFields = [
    { label: "From", field: "from_location" },
    { label: "To", field: "to_location" },
    { label: "Loading Date", field: "loading_date", type: "date" },
    { label: "Unloading Date", field: "unloading_date", type: "date" },
    { label: "Truck No", field: "truck_no" },
    { label: "Truck Owner", field: "truck_owner" },
    { label: "Phone No.", field: "phone_no" },
    { label: "PAN No", field: "pan_no" },
    { label: "TP No", field: "tp_no" },
    { label: "DO No", field: "do_no" },
    { label: "Loading Wt", field: "loading_wt", type: "number" },
    { label: "Receiving Wt", field: "received_wt", type: "number" },
    { label: "LR Rate", field: "freight_rate", type: "number" },
    { label: "Shortage Rate", field: "shortage_rate", type: "number" },
    { label: "TDS %", field: "tds_per", type: "number" },
    { label: "Commission", field: "commission", type: "number" },
    { label: "Ad Mines", field: "ad_mines_exp", type: "number" },
    { label: "Ad Diesel", field: "ad_diesel_exp", type: "number" },
    { label: "Ad Cash", field: "ad_cash_exp", type: "number" },
    { label: "Others Exp.", field: "other_exp", type: "number" },
  ];

  // ── Build table rows with subtotal rows ──────────────────────────
  const buildTableRows = () => {
    const rows = [];
    freightOptions.forEach((row, idx) => {
      const pkgs =
        row.package_items && row.package_items.length > 0
          ? row.package_items
          : [{}];
      const rowSpan = pkgs.length;
      const freightBase = idx % 2 === 0 ? T.baseRow : T.altRow;
      const isSelected = selectedFreightIds.includes(row.freight_id);
      const subtotal = getRowSubtotal(row);

      pkgs.forEach((pkg, pkgIdx) => {
        const rateKey = `${row.freight_id}_${pkgIdx}`;
        const rate = packageRates[rateKey] ?? pkg.rate ?? "0";
        const weight =
          parseFloat(pkg.charged_weight || pkg.actual_weight || 0) || 0;

        // ★ UPDATED: amount calculation
        const numeric = isNumericRate(rate);
        const amount = numeric
          ? ((parseFloat(rate) || 0) * weight).toFixed(2)
          : (parseFloat(fixedAmounts[rateKey]) || 0).toFixed(2);

        const isFirst = pkgIdx === 0;

        const rowBg = row.is_remember
          ? "#fff4e6"
          : pkgIdx % 2 === 0
            ? freightBase
            : freightBase === T.baseRow
              ? "#f0f4ff"
              : "#f7f7f7";

        rows.push({
          type: "data",
          key: `${row.freight_id}_${pkgIdx}`,
          row,
          idx,
          pkg,
          pkgIdx,
          rateKey,
          rate,
          weight,
          amount,
          numeric, // ★ NEW
          isFirst,
          rowSpan,
          rowBg,
          isSelected,
        });
      });

      rows.push({
        type: "subtotal",
        key: `subtotal_${row.freight_id}`,
        row,
        idx,
        subtotal,
        pkgCount: pkgs.length,
      });
    });
    return rows;
  };

  const STICKY_SHADOW = "2px 0 5px rgba(30,27,75,0.06)";

  // ─────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -4, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Header Card ── */}
      <Paper
        elevation={0}
        sx={{
          ...sq,
          border: `1px solid ${T.border}`,
          overflow: "hidden",
          mb: 1.5,
          mt: 2,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            backgroundColor: "#f8fafc",
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* ── Back button in edit mode ── */}
            {isEditMode && (
              <IconButton
                size="small"
                onClick={() => navigate(-1)}
                sx={{
                  ...sq,
                  bgcolor: T.white,
                  border: `1.5px solid ${T.border}`,
                  color: T.primary,
                  "&:hover": { bgcolor: T.primaryBg },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}

            <Box
              sx={{
                width: 32,
                height: 32,
                ...sq,
                bgcolor: T.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ReceiptIcon sx={{ color: T.primary, fontSize: 17 }} />
            </Box>

            <Box>
              {/* ── Title changes based on mode ── */}
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: "1em",
                  lineHeight: 1.2,
                  textAlign: "left",
                }}
              >
                {isEditMode
                  ? `Edit Bill — #${editState?.billingData?.billing_id}`
                  : "Bill Creation"}
              </Typography>
              <Typography sx={{ fontSize: "0.68rem", color: T.textSub }}>
                {isEditMode
                  ? `Editing bill for ${editState?.billingData?.party_name}`
                  : "Select a party to load pending freights"}
              </Typography>
            </Box>

            <Chip
              label={`${freightOptions.length} LR`}
              size="small"
              sx={{
                ...sq,
                bgcolor: T.white,
                color: T.primary,
                fontSize: "0.62rem",
                fontWeight: 600,
                height: 20,
              }}
            />
          </Stack>

          {/* Edit mode badge */}
          {isEditMode && (
            <Chip
              label="Edit Mode"
              size="small"
              sx={{
                ...sq,
                height: 22,
                bgcolor: "#fef3c7",
                color: "#92400e",
                border: "1px solid #fbbf24",
                fontSize: "0.62rem",
                fontWeight: 700,
              }}
            />
          )}
        </Box>

        <Box sx={{ p: 2, bgcolor: "transparent" }}>
          <Stack spacing={1.5}>
            {/* ── Row 1 ── */}
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={5}>
                <Autocomplete
                  options={partyOptions}
                  getOptionLabel={(o) => o?.name || ""}
                  groupBy={(o) =>
                    o.party_type === "consignee"
                      ? "Consignee"
                      : o.party_type === "broker"
                        ? "Broker"
                        : "Consignor"
                  }
                  isOptionEqualToValue={(o, v) => o.party_id === v.party_id}
                  value={
                    partyOptions.find(
                      (p) => p.party_id === formData.party_id,
                    ) || null
                  }
                  onChange={async (_, v) => {
                    setFormData((p) => ({ ...p, party_id: v?.party_id || "" }));
                    setFormErrors((p) => ({ ...p, party_id: null }));
                    if (v?.tds_percent) {
                      setTdsPercent(parseFloat(v.tds_percent) || 0);
                    } else {
                      setTdsPercent(0);
                      setTdsAmount(0);
                    }
                    if (!isEditMode) {
                      if (v?.party_id) {
                        await getFreightsByPartyId(v.party_id, v.party_type);
                      } else {
                        setFreightOptions([]);
                        setPackageRates({});
                        setFixedAmounts({});
                      }
                    }
                  }}
                  size="small"
                  disabled={isEditMode}
                  sx={{ "& .MuiAutocomplete-inputRoot": { height: 36 } }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.party_id}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.8rem", flex: 1 }}>
                          {option.name}
                        </Typography>
                        <Chip
                          label={
                            option.party_type === "consignee"
                              ? "Consignee"
                              : option.party_type === "broker"
                                ? "Broker" 
                                : "Consignor"
                          }
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            bgcolor:
                              option.party_type === "consignee"
                                ? "#fef3c7"
                                : option.party_type === "broker"
                                  ? "#f0fdf4"
                                  : "#e0e7ff",
                            color:
                              option.party_type === "consignee"
                                ? "#92400e"
                                : option.party_type === "broker"
                                  ? "#15803d" 
                                  : "#3730a3",
                          }}
                        />
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Party Name *"
                      error={!!formErrors.party_id}
                      helperText={formErrors.party_id}
                      sx={filterInputSx}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={3} md={3}>
                <TextField
                  label="Total Payable"
                  value={formatCurrency(grandTotal)}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  size="small"
                  sx={disabledFieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={3} md={4}>
                <Button
                  fullWidth
                  onClick={handleSubmit}
                  startIcon={<ReceiptIcon sx={{ fontSize: 16 }} />}
                  disabled={selectedFreightIds.length === 0 || loading}
                  sx={{ ...primaryBtn, height: 36 }}
                >
                  {loading ? (
                    <CircularProgress size={18} sx={{ color: T.white }} />
                  ) : isEditMode ? (
                    "Update Bill" // ← button label changes
                  ) : (
                    "Create Bill"
                  )}
                </Button>
                {formErrors.freight_id && (
                  <Typography
                    sx={{ fontSize: "0.68rem", color: T.red, mt: 0.5 }}
                  >
                    {formErrors.freight_id}
                  </Typography>
                )}
              </Grid>
            </Grid>
            {/* ── Row 2 ── */}
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={5}>
                <TextField
                  label="Halting Message"
                  value={haltingMessage}
                  onChange={(e) => setHaltingMessage(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Enter halting message"
                  sx={filterInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <TextField
                  label="Halting Charge"
                  type="number"
                  value={haltingCharge}
                  onChange={(e) => setHaltingCharge(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                  sx={{
                    ...filterInputSx,
                    "& .MuiInputLabel-root": { fontSize: "0.78rem" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: T.textMuted,
                          mr: 0.5,
                        }}
                      >
                        ₹
                      </Typography>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={4}>
                <TextField
                  label="Grand Total"
                  value={formatCurrency(grandTotalWithHalting)}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  size="small"
                  sx={{
                    ...disabledFieldSx,
                    "& .MuiInputBase-root": {
                      height: 36,
                      ...sq,
                      bgcolor: `${T.primary}12`,
                    },
                    "& fieldset": { border: `1.5px solid ${T.primary}` },
                    "& .MuiInputBase-input": {
                      fontWeight: 700,
                      color: T.primary,
                    },
                  }}
                />
              </Grid>
            </Grid>
            {/* ── Row 3: TDS & Deduction ── */}
            <Grid container spacing={1.5} alignItems="flex-start">
              {/* TDS % */}
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="TDS %"
                  type="number"
                  value={tdsPercent}
                  onChange={(e) => setTdsPercent(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                  sx={filterInputSx}
                  InputProps={{
                    endAdornment: (
                      <Typography
                        sx={{ fontSize: "0.72rem", color: T.textMuted }}
                      >
                        %
                      </Typography>
                    ),
                  }}
                />
              </Grid>

              {/* TDS Amount — auto-calculated, but manually editable */}
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="TDS Amt (auto)"
                  type="number"
                  value={tdsAmount}
                  onChange={(e) => setTdsAmount(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                  sx={{
                    ...filterInputSx,
                    "& .MuiInputBase-root": {
                      height: 36,
                      ...sq,
                      bgcolor: "#FEF2F2",
                    },
                    "& fieldset": { border: `1.5px solid #FECACA` },
                    "& :hover fieldset": { border: `1.5px solid #F87171` },
                    "& .Mui-focused fieldset": {
                      border: `1.5px solid #EF4444`,
                    },
                    "& .MuiInputBase-input": {
                      color: "#DC2626",
                      fontWeight: 700,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        sx={{ fontSize: "0.72rem", color: "#DC2626", mr: 0.5 }}
                      >
                        ₹
                      </Typography>
                    ),
                  }}
                />
              </Grid>

              {/* Deduction Amount */}
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  label="Deduction"
                  type="number"
                  value={deductionAmount}
                  onChange={(e) => setDeductionAmount(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                  sx={{
                    ...filterInputSx,
                    "& .MuiInputBase-root": {
                      height: 36,
                      ...sq,
                      bgcolor: "#FFF7ED",
                    },
                    "& fieldset": { border: `1.5px solid #FED7AA` },
                    "& :hover fieldset": { border: `1.5px solid #FB923C` },
                    "& .Mui-focused fieldset": {
                      border: `1.5px solid #EA580C`,
                    },
                    "& .MuiInputBase-input": {
                      color: "#EA580C",
                      fontWeight: 700,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        sx={{ fontSize: "0.72rem", color: "#EA580C", mr: 0.5 }}
                      >
                        ₹
                      </Typography>
                    ),
                  }}
                />
              </Grid>

              {/* Deduction Remarks */}
              <Grid item xs={6} sm={3} md={3}>
                <TextField
                  label="Deduction Reason"
                  value={deductionRemarks}
                  onChange={(e) => setDeductionRemarks(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="e.g. Short delivery, damage..."
                  sx={filterInputSx}
                />
              </Grid>

              {/* Final Net Payable — read only summary */}
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    height: 36,
                    border: `1.5px solid #A7F3D0`,
                    bgcolor: "#ECFDF5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.25,
                    ...sq,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      color: "#059669",
                    }}
                  >
                    Net Payable
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 800,
                      color: "#059669",
                    }}
                  >
                    {formatCurrency(finalNetPayable)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      </Paper>

      {/* ── Mobile View ── */}
      {isMobile ? (
        <Box>
          {freightOptions.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                ...sq,
                border: `1.5px solid ${T.border}`,
                bgcolor: T.primaryBg,
                p: 1,
                mb: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    sx={{
                      color: T.primary,
                      "&.Mui-checked": { color: T.primary },
                      p: 0.5,
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{ fontSize: "0.75rem", fontWeight: 600, color: T.text }}
                  >
                    Select All ({selectedFreightIds.length}/
                    {freightOptions.length})
                  </Typography>
                }
              />
              {selectedFreightIds.length > 0 && (
                <Chip
                  label={formatCurrency(finalNetPayable)}
                  size="small"
                  sx={{
                    ...sq,
                    bgcolor: T.primary,
                    color: T.white,
                    fontSize: "0.68rem",
                    fontWeight: 600,
                  }}
                />
              )}
            </Paper>
          )}

          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 10,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={32} />
              <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
                Loading…
              </Typography>
            </Box>
          ) : freightOptions.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                border: `1.5px dashed ${T.border}`,
                bgcolor: T.primaryBg,
                ...sq,
              }}
            >
              <ReceiptIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
              <Typography
                sx={{ fontWeight: 700, color: T.text, fontSize: "0.9rem" }}
              >
                No freights found. Please select a party.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}>
              {freightOptions.map((row, idx) => (
                <MobileBillCard
                  key={row.freight_id}
                  row={row}
                  idx={idx}
                  expanded={expandedCardId === row.freight_id}
                  onToggle={() => handleCardToggle(row.freight_id)}
                  selected={selectedFreightIds.includes(row.freight_id)}
                  onSelect={() => handleRowSelect(row.freight_id)}
                  onEdit={handleEditOpen}
                  onDelete={(e, id) => {
                    setDeleteAnchorEl(e.currentTarget);
                    setDeleteId(id);
                  }}
                  onPrint={handlePrintSingleRow}
                  packageRates={packageRates}
                  onRateChange={handleRateChange}
                  fixedAmounts={fixedAmounts}
                  onFixedAmountChange={handleFixedAmountChange}
                />
              ))}

              {/* Mobile Grand Total */}
              <Paper
                elevation={0}
                sx={{
                  ...sq,
                  border: `2px solid ${T.grandTotalBorder}`,
                  bgcolor: T.grandTotalBg,
                  p: 1.5,
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    color: T.grandTotalText,
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  📊 Grand Total
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 800,
                    color: T.grandTotalText,
                  }}
                >
                  {formatCurrency(grandTotal)}
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      ) : (
        /* ── Desktop Table ── */
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
          }}
        >
          <TableContainer
            sx={{ maxHeight: "calc(100vh - 300px)", overflowX: "auto" }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 900,
                borderCollapse: "separate",
                borderSpacing: 0,
                "& td, & th": {
                  py: 0,
                  px: 0.75,
                  fontSize: "0.75rem",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: `1px solid #ccc`,
                  borderRight: `1px solid #ccc`,
                  lineHeight: 1.2,
                },
                "& th": {
                  fontWeight: 700,
                  color: T.headerText,
                  bgcolor: `${T.headerBg} !important`,
                  py: 0.2,
                  height: 28,
                  fontSize: "0.8rem",
                  letterSpacing: "0.01em",
                  borderBottom: `1px solid #ccc`,
                  lineHeight: 1.2,
                },
                "& td": { height: 26 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      top: 0,
                      zIndex: 20,
                      bgcolor: `${T.headerBg} !important`,
                      minWidth: 40,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      sx={{
                        color: T.headerText,
                        "&.Mui-checked": { color: T.primary },
                        p: 0.25,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 40,
                      top: 0,
                      zIndex: 20,
                      bgcolor: `${T.headerBg} !important`,
                      minWidth: 35,
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 75,
                      top: 0,
                      zIndex: 20,
                      bgcolor: `${T.headerBg} !important`,
                      minWidth: 90,
                      boxShadow: STICKY_SHADOW,
                    }}
                  >
                    LR No
                  </TableCell>
                  {[
                    { l: "LR Date", w: 85 },
                    { l: "Vehicle No", w: 90 },
                    { l: "From", w: 80 },
                    { l: "To", w: 80 },
                    { l: "Package", w: 120 },
                    { l: "Actual\u00a0Wt", w: 80 },
                    { l: "Charged\u00a0Wt", w: 80 },
                    { l: "Rate", w: 90 },
                    { l: "Amount", w: 100 },
                  ].map(({ l, w }) => (
                    <TableCell
                      key={l}
                      sx={{
                        minWidth: w,
                        bgcolor: `${T.headerBg} !important`,
                      }}
                    >
                      {l}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading && freightOptions.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMN_COUNT}
                      sx={{ textAlign: "center", py: 6, border: "none" }}
                    >
                      <CircularProgress size={28} sx={{ color: T.primary }} />
                    </TableCell>
                  </TableRow>
                ) : freightOptions.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMN_COUNT}
                      sx={{ textAlign: "center", py: 6, border: "none" }}
                    >
                      <ReceiptIcon
                        sx={{
                          fontSize: 40,
                          color: T.border,
                          mb: 1,
                          display: "block",
                          mx: "auto",
                        }}
                      />
                      <Typography
                        sx={{
                          color: T.textSub,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                        }}
                      >
                        No freights found. Please select a party.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  buildTableRows().map((item) => {
                    if (item.type === "subtotal") {
                      const bg = item.idx % 2 === 0 ? T.baseRow : T.altRow;
                      return (
                        <TableRow key={item.key}>
                          <TableCell
                            colSpan={3}
                            sx={{
                              position: "sticky",
                              left: 0,
                              zIndex: 10,
                              bgcolor: `${bg} !important`,
                            }}
                          />
                          <TableCell colSpan={4} sx={{ bgcolor: bg }} />
                          <TableCell
                            colSpan={4}
                            sx={{
                              bgcolor: T.subtotalBg,
                              borderTop: `1.5px solid ${T.subtotalBorder}`,
                              borderBottom: `1.5px solid ${T.subtotalBorder}`,
                              textAlign: "right",
                              fontWeight: 700,
                              color: T.subtotalText,
                              fontSize: "0.65rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.03em",
                              pr: "12px",
                            }}
                          >
                            Subtotal:
                          </TableCell>
                          <TableCell
                            sx={{
                              bgcolor: T.subtotalBg,
                              borderTop: `1.5px solid ${T.subtotalBorder}`,
                              borderBottom: `1.5px solid ${T.subtotalBorder}`,
                              fontWeight: 800,
                              color: T.subtotalText,
                              fontSize: "0.72rem",
                            }}
                          >
                            {formatCurrency(item.subtotal)}
                          </TableCell>
                        </TableRow>
                      );
                    }

                    // ── Data Row ──
                    const {
                      row,
                      idx,
                      pkg,
                      pkgIdx,
                      rateKey,
                      rate,
                      weight,
                      amount,
                      numeric,
                      isFirst,
                      rowSpan,
                      rowBg,
                      isSelected,
                    } = item;
                    const sc = {
                      bgcolor: `${item.rowBg} !important`,
                      zIndex: 10,
                    };

                    return (
                      <TableRow
                        key={item.key}
                        sx={{
                          "& > td": {
                            bgcolor: item.rowBg,
                            transition: "background-color 0.12s",
                          },
                          "&:hover > td": {
                            bgcolor: `${T.rowHover} !important`,
                          },
                        }}
                      >
                        {isFirst && (
                          <>
                            <TableCell
                              rowSpan={rowSpan}
                              sx={{
                                position: "sticky",
                                left: 0,
                                ...sc,
                                verticalAlign: "middle",
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                onChange={() => handleRowSelect(row.freight_id)}
                                sx={{
                                  color: T.primary,
                                  "&.Mui-checked": { color: T.success },
                                  p: 0,
                                }}
                              />
                            </TableCell>
                            <TableCell
                              rowSpan={rowSpan}
                              sx={{
                                position: "sticky",
                                left: 40,
                                ...sc,
                                color: T.textMuted,
                                fontWeight: 600,
                                verticalAlign: "middle",
                              }}
                            >
                              {idx + 1}
                            </TableCell>
                            <TableCell
                              rowSpan={rowSpan}
                              sx={{
                                position: "sticky",
                                left: 75,
                                ...sc,
                                fontWeight: 700,
                                color: T.primary,
                                verticalAlign: "middle",
                              }}
                            >
                              {row.lr_number || "—"}
                            </TableCell>
                            <TableCell
                              rowSpan={rowSpan}
                              sx={{ verticalAlign: "middle", ...sc }}
                            >
                              {formatDate(row.loading_date)}
                            </TableCell>
                            <TableCell
                              rowSpan={rowSpan}
                              sx={{
                                verticalAlign: "middle",
                                fontWeight: 600,
                                color: T.text,
                                ...sc,
                              }}
                            >
                              {row.truck_no || "—"}
                            </TableCell>
                            <TableCell
                              rowSpan={rowSpan}
                              sx={{
                                maxWidth: 80,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                verticalAlign: "middle",
                                ...sc,
                              }}
                            >
                              {row.from_location || "—"}
                            </TableCell>
                            <TableCell
                              rowSpan={rowSpan}
                              sx={{
                                maxWidth: 80,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                verticalAlign: "middle",
                                ...sc,
                              }}
                            >
                              {row.to_location || "—"}
                            </TableCell>
                          </>
                        )}

                        <TableCell sx={{ ...sc }}>
                          <Typography
                            sx={{
                              fontSize: "0.68rem",
                              fontWeight: 600,
                              color: T.text,
                            }}
                          >
                            {pkg.packages || "—"}
                          </Typography>
                          {pkg.description && (
                            <Typography
                              sx={{ fontSize: "0.58rem", color: T.textMuted }}
                            >
                              {pkg.description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...sc }}>
                          {pkg.actual_weight ?? "—"}
                        </TableCell>
                        <TableCell sx={{ ...sc }}>
                          {pkg.charged_weight ?? "—"}
                        </TableCell>

                        {/* ★ RATE CELL — type="text" */}
                        <TableCell sx={{ ...sc, p: "3px 6px" }}>
                          <TextField
                            size="small"
                            type="text"
                            value={rate}
                            onChange={(e) =>
                              handleRateChange(rateKey, e.target.value)
                            }
                            disabled={!isSelected}
                            placeholder="Rate"
                            sx={{
                              ...rateInputSx,
                              width: 80,
                              // ★ Orange border when character (non-numeric)
                              ...(!numeric && {
                                "& .MuiInputBase-root": {
                                  bgcolor: "#fffbeb",
                                  border: "1.5px solid #f59e0b",
                                  height: 26,
                                  fontSize: "0.68rem",
                                  ...sq,
                                },
                                "& fieldset": {
                                  border: "none",
                                },
                              }),
                              "& .MuiInputBase-root.Mui-disabled": {
                                bgcolor: `${T.textMuted}22`,
                                cursor: "not-allowed",
                              },
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: T.textMuted,
                              },
                            }}
                            inputProps={{
                              style: {
                                padding: "3px 6px",
                                fontSize: "0.68rem",
                              },
                            }}
                          />
                        </TableCell>

                        {/* ★ AMOUNT CELL — editable when character rate */}
                        <TableCell
                          sx={{
                            ...sc,
                            fontWeight: 700,
                            color: T.primary,
                            p: "3px 6px",
                          }}
                        >
                          {numeric ? (
                            // Number rate → auto calculated, read-only
                            formatCurrency(amount)
                          ) : (
                            // Character rate → editable amount
                            <TextField
                              size="small"
                              type="number"
                              value={fixedAmounts[rateKey] ?? ""}
                              onChange={(e) =>
                                handleFixedAmountChange(rateKey, e.target.value)
                              }
                              disabled={!isSelected}
                              placeholder="₹ Amount"
                              sx={{
                                ...rateInputSx,
                                width: 85,
                                "& .MuiInputBase-root": {
                                  bgcolor: "#fffbeb",
                                  border: "1.5px solid #fbbf24",
                                  fontWeight: 700,
                                  height: 26,
                                  fontSize: "0.68rem",
                                  ...sq,
                                },
                                "& fieldset": {
                                  border: "none",
                                },
                                "& .MuiInputBase-root:hover": {
                                  border: "1.5px solid #f59e0b",
                                },
                                "& .MuiInputBase-root.Mui-focused": {
                                  border: "1.5px solid #d97706",
                                },
                                "& .MuiInputBase-root.Mui-disabled": {
                                  bgcolor: `${T.textMuted}15`,
                                  border: `1.5px solid ${T.border}`,
                                },
                              }}
                              inputProps={{
                                min: 0,
                                style: {
                                  padding: "3px 6px",
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                },
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>

              {freightOptions.length > 0 && (
                <TableFooter>
                  <TableRow
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      zIndex: 15,
                      "& td": {
                        bgcolor: `${T.grandTotalBg} !important`,
                        fontWeight: 600,
                        fontSize: "0.68rem",
                        py: 0.75,
                        borderTop: `2px solid ${T.grandTotalBorder}`,
                        borderBottom: `2px solid ${T.grandTotalBorder}`,
                      },
                    }}
                  >
                    <TableCell
                      colSpan={3}
                      align="right"
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 16,
                        bgcolor: `${T.grandTotalBg} !important`,
                        color: T.grandTotalText,
                        fontWeight: 800,
                        fontSize: "0.72rem !important",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        boxShadow: STICKY_SHADOW,
                      }}
                    >
                      Grand Total
                    </TableCell>
                    <TableCell colSpan={8} />
                    <TableCell
                      sx={{
                        color: `${T.grandTotalText} !important`,
                        fontWeight: 800,
                        fontSize: "0.78rem !important",
                        textAlign: "right",
                      }}
                    >
                      {formatCurrency(grandTotal)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Edit Dialog ── */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: T.primary,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 30,
                height: 30,
                ...sq,
                bgcolor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ color: T.white, fontSize: 16 }} />
            </Box>
            <Typography
              sx={{ fontWeight: 700, color: T.white, fontSize: "0.9rem" }}
            >
              Edit LR Record
            </Typography>
          </Stack>
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            size="small"
            sx={{
              color: T.white,
              ...sq,
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2.5 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            {[
              { label: "Remember", field: "is_remember" },
              { label: "LR Not Received", field: "is_not_rec" },
            ].map(({ label, field }) => (
              <FormControlLabel
                key={field}
                control={
                  <Checkbox
                    checked={editRow[field] || false}
                    onChange={(e) =>
                      field === "is_remember"
                        ? setEditRow((p) => ({
                            ...p,
                            is_remember: e.target.checked,
                          }))
                        : handleEditChange(field, e.target.checked)
                    }
                    sx={{
                      color: T.primary,
                      "&.Mui-checked": { color: T.primary },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.8rem" }}>{label}</Typography>
                }
              />
            ))}
          </Stack>

          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={3}>
              <Autocomplete
                options={partyOptions}
                getOptionLabel={(o) => o?.name || ""}
                value={
                  partyOptions.find((o) => o.name === editRow.party_name) ||
                  null
                }
                onChange={(_, v) =>
                  handleEditChange("party_id", v?.party_id || "")
                }
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Party Name"
                    sx={filterInputSx}
                  />
                )}
              />
            </Grid>
            {editFields.map(({ label, field, type }) => (
              <Grid item xs={6} sm={3} key={field}>
                <TextField
                  label={label}
                  fullWidth
                  size="small"
                  type={type || "text"}
                  value={editRow[field] || ""}
                  onChange={(e) => handleEditChange(field, e.target.value)}
                  InputLabelProps={
                    type === "date" ? { shrink: true } : undefined
                  }
                  sx={filterInputSx}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <TextField
                label="Remark"
                fullWidth
                multiline
                rows={2}
                size="small"
                value={editRow.remark || ""}
                onChange={(e) => handleEditChange("remark", e.target.value)}
                sx={filterInputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: T.primaryBg,
            borderTop: `1px solid ${T.border}`,
            gap: 1,
          }}
        >
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ ...ghostBtn, px: 2 }}
          >
            Cancel
          </Button>
          <Button onClick={handleEditSave} sx={{ ...primaryBtn, px: 2 }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Popover ── */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={() => {
          setDeleteAnchorEl(null);
          setDeleteId(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
            bgcolor: T.redBg,
          },
        }}
      >
        <Box sx={{ p: 2, maxWidth: 280 }}>
          <Typography
            sx={{
              fontWeight: 700,
              mb: 0.5,
              fontSize: "0.85rem",
              color: T.text,
            }}
          >
            Delete LR Record?
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: T.textSub, mb: 2 }}>
            This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              size="small"
              onClick={() => {
                setDeleteAnchorEl(null);
                setDeleteId(null);
              }}
              sx={{ ...ghostBtn, px: 1.5, fontSize: "0.72rem" }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              onClick={() => handleDelete(deleteId)}
              sx={{
                ...sq,
                bgcolor: T.red,
                color: T.white,
                fontSize: "0.72rem",
                textTransform: "none",
                px: 1.5,
                boxShadow: "none",
                "&:hover": { bgcolor: "#b91c1c", boxShadow: "none" },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: "80px !important" }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ ...sq, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BillCreationForm;
